import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Link2, FileText } from 'lucide-react';
import { useAuth } from '../../../shared/auth/hooks/useAuth';
import { SignupData, FormErrors } from '../../../shared/auth/types';
import { validateSignupForm, hasErrors } from '../../../shared/auth/utils/validation';
import { AUTH_ROUTES } from '../../../shared/auth/constants';
import { FileUpload, Textarea } from '../../../shared/components/ui';
import { AuthLayout, InputField, Checkbox } from '../../../shared/components/modern';

const CandidateSignUp: React.FC = () => {
    const navigate = useNavigate();
    const { register, isLoading, error, clearError, isAuthenticated } = useAuth();

    const [signupData, setSignupData] = useState<SignupData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        professionalRole: 'Engineering & Development', // Default value since no professional role field in form
        agreeToTerms: false,
        userType: 'candidate' // Explicitly set as candidate signup
    });

    const [linkedInLink, setLinkedInLink] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [manualResume, setManualResume] = useState('');
    const [inputMethod, setInputMethod] = useState<'upload' | 'linkedin' | 'manual' | null>(null);

    // Redirect to success page after successful signup
    useEffect(() => {
        if (isAuthenticated) {
            // After successful signup, always redirect to success page with flow state
            navigate('/auth/candidate/signup-success', {
                replace: true,
                state: { fromCandidateSignup: true }
            });
        }
    }, [isAuthenticated, navigate]);

    const handleInputChange = (field: keyof SignupData, value: string | boolean) => {
        setSignupData(prev => ({
            ...prev,
            [field]: value,
        }));

        if (fieldErrors[field as keyof FormErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }

        if (error) {
            clearError();
        }
    };

    const handleFileUpload = (file: File) => {
        console.log('Resume file selected:', file.name);
        setResumeFile(file);
        setInputMethod('upload');
        setLinkedInLink('');
        setManualResume('');
        // TODO: Upload file to backend API
    };

    const handleFileRemove = () => {
        setResumeFile(null);
        setInputMethod(null);
    };

    const handleLinkedInChange = (value: string) => {
        setLinkedInLink(value);
        if (value) {
            setInputMethod('linkedin');
            setResumeFile(null);
            setManualResume('');
        } else {
            setInputMethod(null);
        }
    };

    const handleManualResumeChange = (value: string) => {
        setManualResume(value);
        if (value) {
            setInputMethod('manual');
            setResumeFile(null);
            setLinkedInLink('');
        } else {
            setInputMethod(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const errors = validateSignupForm(signupData);
        setFieldErrors(errors);

        if (hasErrors(errors)) {
            return;
        }

        try {
            await register(signupData);
        } catch (err) {
            if (err instanceof Error) {
                if (err.message === 'EMAIL_ALREADY_EXISTS' || err.message.toLowerCase().includes('already exists')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        email: 'This Email address already exist'
                    }));
                } else if (err.message.toLowerCase().includes('password')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        password: err.message
                    }));
                }
            }
        }
    };

    return (
        <AuthLayout
            title="Apply Now"
            subtitle={
                <>
                    Already have an account? <a href={AUTH_ROUTES.LOGIN} className="text-[#015FA6] font-bold underline decoration-2 underline-offset-4 hover:text-[#043362] transition-colors">Sign in</a>
                </>
            }
            heroVariant="candidate"
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
                {/* First Name & Last Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <InputField
                        id="firstName"
                        label="First name"
                        type="text"
                        value={signupData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Ben"
                        error={fieldErrors.firstName}
                        delay={100}
                    />
                    <InputField
                        id="lastName"
                        label="Last name"
                        type="text"
                        value={signupData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Pucci"
                        error={fieldErrors.lastName}
                        delay={150}
                    />
                </div>

                {/* Email & Password Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <InputField
                        id="email"
                        label="Email"
                        type="email"
                        value={signupData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="example@gmail.com"
                        error={fieldErrors.email}
                        delay={200}
                    />
                    <InputField
                        id="password"
                        label="Password"
                        type="password"
                        value={signupData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="@#*%"
                        error={fieldErrors.password}
                        delay={250}
                    />
                </div>

                {/* Resume Input Section */}
                <div className="space-y-4 animate-fade-in-up opacity-0" style={{ animationDelay: '300ms' }}>
                    <label className="block text-base font-semibold text-[#015FA6] font-manrope">
                        Resume / Profile Information
                    </label>

                    {/* Option Selection Tabs */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <button
                            type="button"
                            onClick={() => setInputMethod(inputMethod === 'upload' ? null : 'upload')}
                            className={`flex flex-col items-center gap-2 py-4 px-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 ${inputMethod === 'upload'
                                    ? 'bg-[#015FA6] text-white shadow-lg scale-[1.02]'
                                    : 'bg-white text-[#015FA6] border border-[#CBD5E0] hover:border-[#015FA6] hover:shadow-md hover:-translate-y-0.5'
                                }`}
                        >
                            <Upload className="w-5 h-5" />
                            <span>Upload File</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setInputMethod(inputMethod === 'linkedin' ? null : 'linkedin')}
                            className={`flex flex-col items-center gap-2 py-4 px-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 ${inputMethod === 'linkedin'
                                    ? 'bg-[#015FA6] text-white shadow-lg scale-[1.02]'
                                    : 'bg-white text-[#015FA6] border border-[#CBD5E0] hover:border-[#015FA6] hover:shadow-md hover:-translate-y-0.5'
                                }`}
                        >
                            <Link2 className="w-5 h-5" />
                            <span>LinkedIn</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setInputMethod(inputMethod === 'manual' ? null : 'manual')}
                            className={`flex flex-col items-center gap-2 py-4 px-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 ${inputMethod === 'manual'
                                    ? 'bg-[#015FA6] text-white shadow-lg scale-[1.02]'
                                    : 'bg-white text-[#015FA6] border border-[#CBD5E0] hover:border-[#015FA6] hover:shadow-md hover:-translate-y-0.5'
                                }`}
                        >
                            <FileText className="w-5 h-5" />
                            <span>Enter Manually</span>
                        </button>
                    </div>

                    {/* File Upload */}
                    {inputMethod === 'upload' && (
                        <div className="animate-fade-in-up">
                            <FileUpload
                                accept=".pdf,.doc,.docx"
                                maxSize={5 * 1024 * 1024}
                                onFileSelect={handleFileUpload}
                                onFileRemove={handleFileRemove}
                                currentFile={resumeFile}
                                helperText="Upload your resume in PDF, DOC, or DOCX format (max 5MB)"
                            />
                        </div>
                    )}

                    {/* LinkedIn URL */}
                    {inputMethod === 'linkedin' && (
                        <div className="animate-fade-in-up">
                            <InputField
                                id="linkedin"
                                label="LinkedIn URL"
                                type="url"
                                value={linkedInLink}
                                onChange={(e) => handleLinkedInChange(e.target.value)}
                                placeholder="https://linkedin.com/in/your-profile"
                            />
                        </div>
                    )}

                    {/* Manual Resume Entry */}
                    {inputMethod === 'manual' && (
                        <div className="animate-fade-in-up">
                            <Textarea
                                value={manualResume}
                                onChange={(e) => handleManualResumeChange(e.target.value)}
                                placeholder="Enter your work experience, education, skills, and achievements here..."
                                rows={12}
                                helperText="Provide your resume details including work experience, education, and skills"
                                fullWidth
                                className="rounded-xl border-[#CBD5E0] focus:border-[#015FA6] focus:ring-[#015FA6]/10"
                            />
                        </div>
                    )}

                    {!inputMethod && (
                        <div className="text-center py-8 border-2 border-dashed border-[#CBD5E0] rounded-xl bg-[#F8F9FB]/50">
                            <p className="text-sm text-gray-500 font-manrope">
                                Select an option above to provide your resume information
                            </p>
                        </div>
                    )}
                </div>

                {/* Terms Checkbox */}
                <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '400ms' }}>
                    <Checkbox
                        checked={signupData.agreeToTerms}
                        onChange={(checked) => handleInputChange('agreeToTerms', checked)}
                        error={fieldErrors.agreeToTerms}
                        label={
                            <span className="text-[#015FA6]">
                                I agree to the{' '}
                                <span className="text-[#F59E0B] font-semibold">Terms of Use</span> and{' '}
                                <span className="text-[#F59E0B] font-semibold">Privacy Policy</span>
                            </span>
                        }
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-fade-in-up">
                        <p className="text-sm text-red-600 font-manrope">{error}</p>
                    </div>
                )}

                {/* Sign Up Button */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="
                        w-full h-[60px] mt-4 rounded-xl font-bold text-lg text-white
                        bg-gradient-to-r from-[#015FA6] to-[#0172C6]
                        hover:shadow-[0_8px_25px_-5px_rgba(1,95,166,0.4)]
                        active:scale-[0.98]
                        transition-all duration-300
                        animate-fade-in-up opacity-0
                        flex items-center justify-center gap-2
                        disabled:opacity-70 disabled:cursor-not-allowed
                    "
                    style={{ animationDelay: '500ms' }}
                >
                    {isLoading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Creating Account...
                        </>
                    ) : (
                        'Sign Up'
                    )}
                </button>
            </form>
        </AuthLayout>
    );
};

export default CandidateSignUp;
